// sequenceFormHelpers — pure builders / parsers for SequenceEditFormBody.
//
// Extracted into a sibling non-TSX module to (1) keep the form component
// under the 200-line budget per CLAUDE.md, and (2) avoid the
// react-refresh/only-export-components lint trap that fires when a TSX file
// exports both a component and runtime helpers.
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D3 — Sequence UI.

import type { QualifiedName, SqlEngine } from '../../../../sql-grammar/model';
import type {
  SequenceChange,
  SequenceDef,
} from '../../../../sql-resolver/types/ddlEdit';

export interface SequenceFormValues {
  /** Bare or schema-qualified name. */
  readonly name: string;
  /** mssql / pgsql `AS <type>`. Empty string omits. Oracle rejects this. */
  readonly dataType?: string;
  /** `START WITH n`. Empty string omits. CREATE-only. */
  readonly startWith?: string;
  readonly incrementBy?: string;
  /** `MINVALUE n` / `''` (omit) / `'NONE'` (NO MINVALUE / NOMINVALUE). */
  readonly minValue?: string;
  readonly maxValue?: string;
  /** Tri-state: `''` (omit), `'YES'` (CYCLE), `'NO'` (NO CYCLE). */
  readonly cycle?: string;
  /** `CACHE n` / `''` (omit) / `'NONE'` (NO CACHE / NOCACHE). */
  readonly cache?: string;
}

export const NONE_SENTINEL = 'NONE';

export function parseNumberOrUndefined(text: string | undefined): number | undefined {
  if (text === undefined) return undefined;
  const trimmed = text.trim();
  if (trimmed.length === 0) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return Number.NaN;
  return n;
}

/** `''` → undefined, `'NONE'` → null (engine `NO …` clause), else parsed
 *  number. NaN flags garbage so the validator can surface a friendly error. */
export function parseNumberOrNullSentinel(
  text: string | undefined,
): number | null | undefined {
  if (text === undefined) return undefined;
  const trimmed = text.trim();
  if (trimmed.length === 0) return undefined;
  if (trimmed.toUpperCase() === NONE_SENTINEL) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return Number.NaN;
  return n;
}

export function parseCycle(text: string | undefined): boolean | undefined {
  if (text === undefined) return undefined;
  const trimmed = text.trim().toUpperCase();
  if (trimmed === 'YES' || trimmed === 'CYCLE' || trimmed === 'TRUE') return true;
  if (trimmed === 'NO' || trimmed === 'NO CYCLE' || trimmed === 'FALSE') return false;
  return undefined;
}

export function buildSequenceDef(
  values: SequenceFormValues,
  engine: SqlEngine,
  fallbackName: QualifiedName,
): SequenceDef {
  const trimmedName = values.name.trim();
  const name: QualifiedName = trimmedName === fallbackName.name
    ? fallbackName
    : {
        ...fallbackName,
        name: trimmedName,
        raw: trimmedName,
        foldedName: trimmedName.toLowerCase(),
      };
  const def: Record<string, unknown> = { name };
  if (engine !== 'oracle' && values.dataType && values.dataType.trim().length > 0) {
    def.mssqlAs = values.dataType.trim();
  }
  const startWith = parseNumberOrUndefined(values.startWith);
  if (startWith !== undefined && !Number.isNaN(startWith)) def.startWith = startWith;
  const incrementBy = parseNumberOrUndefined(values.incrementBy);
  if (incrementBy !== undefined && !Number.isNaN(incrementBy)) def.incrementBy = incrementBy;
  const minValue = parseNumberOrNullSentinel(values.minValue);
  if (minValue !== undefined && !Number.isNaN(minValue as number)) def.minValue = minValue;
  const maxValue = parseNumberOrNullSentinel(values.maxValue);
  if (maxValue !== undefined && !Number.isNaN(maxValue as number)) def.maxValue = maxValue;
  const cycle = parseCycle(values.cycle);
  if (cycle !== undefined) def.cycle = cycle;
  const cache = parseNumberOrNullSentinel(values.cache);
  if (cache !== undefined && !Number.isNaN(cache as number)) def.cache = cache;
  return def as SequenceDef;
}

export function buildSequenceChange(values: SequenceFormValues): SequenceChange {
  const change: Record<string, unknown> = {};
  const incrementBy = parseNumberOrUndefined(values.incrementBy);
  if (incrementBy !== undefined && !Number.isNaN(incrementBy)) change.incrementBy = incrementBy;
  const minValue = parseNumberOrNullSentinel(values.minValue);
  if (minValue !== undefined && !Number.isNaN(minValue as number)) change.minValue = minValue;
  const maxValue = parseNumberOrNullSentinel(values.maxValue);
  if (maxValue !== undefined && !Number.isNaN(maxValue as number)) change.maxValue = maxValue;
  const cycle = parseCycle(values.cycle);
  if (cycle !== undefined) change.cycle = cycle;
  const cache = parseNumberOrNullSentinel(values.cache);
  if (cache !== undefined && !Number.isNaN(cache as number)) change.cache = cache;
  return change as SequenceChange;
}
